import React, { useEffect, useState, useCallback } from 'react';
import {
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useDatabases } from '../hooks/useDatabases';
import { toast } from 'react-toastify';

export function DatabasesList({ refresh = 0 }) {
  const { list, remove, loading } = useDatabases();
  const [databases, setDatabases] = useState([]);

  const loadDatabases = useCallback(async () => {
    try {
      const data = await list();
      setDatabases(data);
    } catch (error) {
      toast.error(`Failed to load databases: ${error.message}`);
    }
  }, [list]);

  useEffect(() => {
    loadDatabases();
  }, [loadDatabases]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this database configuration?')) {
      return;
    }

    try {
      await remove(id);
      toast.success('Database deleted successfully');
      setDatabases((prev) => prev.filter((db) => db.id !== id));
    } catch (error) {
      toast.error(`Failed to delete: ${error.message}`);
    }
  };

  if (loading && databases.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Loading databases...</p>
      </div>
    );
  }

  if (databases.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">No database connections configured</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {databases.map((db) => (
        <div
          key={db.id}
          className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between hover:shadow-sm transition"
        >
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">{db.dbName}</h3>
            <div className="mt-2 space-y-1 text-sm text-gray-600">
              <p>User: <span className="font-mono">{db.dbUser}</span></p>
              {db.lastTestedAt && (
                <p className="flex items-center gap-2">
                  {db.lastTestResult === 'success' ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">
                        Last tested: {new Date(db.lastTestedAt).toLocaleString()}
                      </span>
                    </>
                  ) : (
                    <>
                      <ExclamationCircleIcon className="w-4 h-4 text-red-600" />
                      <span className="text-red-700">
                        Test failed: {db.lastTestResult}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>
          </div>

          <button
            onClick={() => handleDelete(db.id)}
            className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-md transition"
            title="Delete"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      ))}
    </div>
  );
}
